import { emptyStringToNull } from '@openx/components-form/emptyStringToNullable';
import { PackageFields, PrivateMarketFields } from '@openx/types/package';
import type { I18nTranslate } from '@openx/types/translations';
import { array, lazy, mixed, number, object, string } from 'yup';

const URL = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/i;

const isNumeric = (value?: string): boolean => value !== undefined && value !== '' && !Number.isNaN(Number(value));
const isNegative = (value?: string): boolean => value !== undefined && value !== '' && parseFloat(value) < 0;
const isValidFormat = (value?: string): boolean =>
  value === '' || value === undefined || /^\d+(\.\d{1,2})?$/.test(value);
const containsWhitespace = (value?: string): boolean => value !== undefined && /\s/.test(value);
const valueIsNotSet = (value?: string | null) => [undefined, null, ''].includes(value);

const getPriceValidation = (t: I18nTranslate) =>
  number()
    .typeError(t('Price has to be a number'))
    .min(0.0001, t('Minimum price is 0.0001'))
    .max(10000, t('Maximum price is 10000'))
    .test(
      'checkDecimalPointDigits',
      t('Price may contain only 4 digits after decimal point'),
      value => !value || (value.toString().match(/^\d+(\.\d{0,4})?$/i) || []).length > 0
    )
    .transform(emptyStringToNull)
    .nullable();

const getRangeValidation = (t: I18nTranslate) =>
  string()
    .test('no-spaces', t('Spaces are not allowed'), value => !value || !containsWhitespace(value))
    .test('is-numeric', t('Value must be numeric'), value => !value || isNumeric(value))
    .test('not-negative', t('Value cannot be negative'), value => !value || !isNegative(value))
    .test('valid-format', t('Value can have up to 2 decimals'), value => !value || isValidFormat(value));

export const getValidationSchema = (t: I18nTranslate) =>
  object().shape(
    {
      [PackageFields.NAME]: string()
        .max(255, t('Name exceeds the maximum number of characters allowed'))
        .required(t('Name is required')),
      [PackageFields.LOGO]: string()
        .matches(URL, t('Logo must be a valid url'))
        .nullable()
        .transform(emptyStringToNull),
      [PackageFields.DOMAINS]: string()
        .max(255, t('Name exceeds the maximum number of characters allowed'))
        .when(`${PackageFields.PRIVATE_MARKET}.${PrivateMarketFields.DISCOVERABLE}`, {
          is: true,
          then: schema => schema.required(t('Domain is required')),
        }),
      [PackageFields.URLS]: array().of(
        string().matches(URL, t('Some of urls are not valid')).max(2000, t('URL is too long')).optional()
      ),
      [PackageFields.RATE]: getPriceValidation(t),
      [PackageFields.PRIVATE_MARKET]: object().shape({
        [PrivateMarketFields.DEFAULT_RATE]: getPriceValidation(t),
        [PrivateMarketFields.DISCOUNTED_RATE]: getPriceValidation(t),
      }),
      [PackageFields.TARGETING]: lazy(value => {
        if (value?.rendering_context) {
          return object().shape({
            rendering_context: object().shape({
              device_type: object()
                .nullable()
                .shape({
                  desktop_devices: string().nullable(),
                  mobile_devices: string().nullable(),
                  tv_devices: string().nullable(),
                })
                .test('required', t('Device is required'), deviceType => {
                  if (!deviceType) {
                    return Boolean(true);
                  }
                  const { desktop_devices, mobile_devices, tv_devices } = deviceType;

                  return Boolean(desktop_devices || mobile_devices || tv_devices);
                }),
              distribution_channel: object()
                .nullable()
                .shape({
                  val: string().required(t('Channel is required')),
                }),
              ad_placement: object()
                .nullable()
                .shape({
                  val: string().required(t('Format is required')),
                }),
            }),
          });
        }
        return mixed().notRequired();
      }),
      [PackageFields.ESTIMATES]: object().when(PackageFields.IS_CURATED, {
        is: true,
        then: schema =>
          schema.shape({
            cpm: object().shape({
              low: getRangeValidation(t).test('pair-required', t('Both fields must be filled'), (value, context) => {
                const highValue = context.parent.high; // Access sibling 'high' field
                if (
                  (valueIsNotSet(highValue) && valueIsNotSet(value)) ||
                  (!valueIsNotSet(highValue) && !valueIsNotSet(value))
                ) {
                  return true;
                }
                return false;
              }),
              high: getRangeValidation(t)
                .test('pair-required', t('Both fields must be filled'), (value, context) => {
                  const lowValue = context.parent.low; // Access sibling 'low' field
                  if (
                    (valueIsNotSet(lowValue) && valueIsNotSet(value)) ||
                    (!valueIsNotSet(lowValue) && !valueIsNotSet(value))
                  ) {
                    return true;
                  }
                  return false;
                })
                .test('from-to-compare', t('From cannot be greater than To'), (value, context) => {
                  const lowValue = context.parent.low; // Access sibling 'low' field
                  return !value || !lowValue || parseFloat(lowValue) <= parseFloat(value);
                }),
            }),
            reach: object().shape({
              low: getRangeValidation(t).test('pair-required', t('Both fields must be filled'), (value, context) => {
                const highValue = context.parent.high; // Access sibling 'high' field
                if (
                  (valueIsNotSet(highValue) && valueIsNotSet(value)) ||
                  (!valueIsNotSet(highValue) && !valueIsNotSet(value))
                ) {
                  return true;
                }
                return false;
              }),
              high: getRangeValidation(t)
                .test('pair-required', t('Both fields must be filled'), (value, context) => {
                  const lowValue = context.parent.low; // Access sibling 'low' field
                  if (
                    (valueIsNotSet(lowValue) && valueIsNotSet(value)) ||
                    (!valueIsNotSet(lowValue) && !valueIsNotSet(value))
                  ) {
                    return true;
                  }
                  return false;
                })
                .test('from-to-compare', t('From cannot be greater than To'), (value, context) => {
                  const lowValue = context.parent.low; // Access sibling 'low' field
                  return !value || !lowValue || parseFloat(lowValue) <= parseFloat(value);
                }),
            }),
            market_requests: object().shape({
              low: getRangeValidation(t).test('pair-required', t('Both fields must be filled'), (value, context) => {
                const highValue = context.parent.high; // Access sibling 'high' field
                if (
                  (valueIsNotSet(highValue) && valueIsNotSet(value)) ||
                  (!valueIsNotSet(highValue) && !valueIsNotSet(value))
                ) {
                  return true;
                }
                return false;
              }),
              high: getRangeValidation(t)
                .test('pair-required', t('Both fields must be filled'), (value, context) => {
                  const lowValue = context.parent.low; // Access sibling 'low' field
                  if (
                    (valueIsNotSet(lowValue) && valueIsNotSet(value)) ||
                    (!valueIsNotSet(lowValue) && !valueIsNotSet(value))
                  ) {
                    return true;
                  }
                  return false;
                })
                .test('from-to-compare', t('From cannot be greater than To'), (value, context) => {
                  const lowValue = context.parent.low; // Access sibling 'low' field
                  return !value || !lowValue || parseFloat(lowValue) <= parseFloat(value);
                }),
            }),
          }),
        otherwise: schema => schema.optional(),
      }),
    },
    [[PackageFields.DOMAINS, PrivateMarketFields.DISCOVERABLE]]
  );
